/* eslint-disable react/prop-types */
/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import { Box } from '@mui/material';
import { useFeatures } from '@paralleldrive/react-feature-toggles';
import React, { Suspense, lazy, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Spinner from '../components/shared/atoms/Spinner';
import { useGeneralContext } from '../context/GeneralContext';
import PrivateRoute from './PrivateRoute';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { currentSession } from '../axios/axiosAuth';
import {
  FALLBACK_INITIAL_POSITION,
  formattedGeoToStr,
  strToGeo,
} from '../utils/geolocation';

const Login = lazy(() => import('../pages/Login'));
const Maps = lazy(() => import('../pages/Maps'));
const DisplayView = lazy(
  () => import('../components/Management/DisplayManagement/DisplayView'),
);
const Facilities = lazy(() => import('../pages/Facilities'));
const Configuration = lazy(() => import('../pages/Configuration'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Management = lazy(() => import('../pages/Management'));
const Reports = lazy(() => import('../pages/Reports'));
const NotFound = lazy(() => import('../pages/NotFound'));

const Routes = () => {
  const features = useFeatures();
  const { data: userPermissions } = useRolePermissions();
  const { t } = useTranslation();
  const management = userPermissions?.management;

  const { lastSelectedLocation, zoom, openToast, resetUser, customization } =
    useGeneralContext();

  let locationUrl;
  if (lastSelectedLocation) {
    locationUrl = formattedGeoToStr(
      lastSelectedLocation.properties.lng,
      lastSelectedLocation.properties.lat,
      zoom,
    );
  } else {
    const { longitude, latitude, zoom } =
      customization.initial_position ||
      process.env.REACT_APP_MAP_INITIAL_POSITION
        ? strToGeo(
            customization.initial_position ||
              process.env.REACT_APP_MAP_INITIAL_POSITION,
          )
        : FALLBACK_INITIAL_POSITION;

    locationUrl = formattedGeoToStr(longitude, latitude, zoom);
  }
  const history = useHistory();

  useEffect(() => {
    const timer = setInterval(async () => {
      if (
        !history.location.pathname.includes('/portal/login') &&
        !history.location.pathname.includes('/portal/resetPassword') &&
        !(await currentSession())
      ) {
        resetUser();
        openToast(t('routes.session_invalid'), 'warning');
        history.push({
          pathname: '/portal/login',
          search: history.location.search,
          state: { from: history.location },
        });
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [history, openToast, resetUser, t]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Suspense
        fallback={
          <Box display="flex" height="100%" alignItems="center">
            <Spinner />
          </Box>
        }
      >
        <Switch>
          <Route path="/portal/login" component={Login} />
          <Route
            exact
            path="/portal/maps"
            render={props => (
              <Redirect
                to={{
                  pathname: `/portal/maps/${locationUrl}`,
                  search: props.location.search,
                }}
              />
            )}
          />

          {features?.includes('management-displays') &&
            management?.displays?.see && (
              <PrivateRoute
                exact
                path="/display/:name"
                component={DisplayView}
                hideNavbar
              />
            )}

          <PrivateRoute exact path="/portal/maps/:location" component={Maps} />

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id"
            component={Facilities}
          />

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id/new"
            component={Facilities}
          />

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id/manage"
            component={Facilities}
          />

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id/sensors/:sensor"
            component={Facilities}
          />

          {features?.includes('zigbee2mqtt') && (
            <PrivateRoute
              exact
              path="/portal/maps/:location?/facilities/:id/actuators/:actuator"
              component={Facilities}
            />
          )}

          {features?.includes('sidemenu-statistics') && (
            <PrivateRoute
              exact
              path="/portal/maps/:location?/facilities/:id/statistics"
              component={Facilities}
            />
          )}

          {/* //----- Sidemenu feature Routes START ----- */}
          {features?.includes('sidemenu-parameters') && (
            <PrivateRoute
              exact
              path="/portal/maps/:location?/facilities/:id/parameters"
              component={Facilities}
            />
          )}

          {features?.includes('sidemenu-parts') && (
            <PrivateRoute
              exact
              path="/portal/maps/:location?/facilities/:id/parts"
              component={Facilities}
            />
          )}

          {features?.includes('sidemenu-openticket') && (
            <PrivateRoute
              exact
              path="/portal/maps/:location?/facilities/:id/openticket"
              component={Facilities}
            />
          )}
          {/* //----- Sidemenu feature Routes END ----- */}

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id/position"
            component={Facilities}
          />

          <PrivateRoute
            exact
            path="/portal/maps/:location?/facilities/:id/monitoring"
            component={Facilities}
          />

          <PrivateRoute
            path="/portal/configuration"
            component={Configuration}
          />
          {userPermissions?.dashboard?.see && (
            <PrivateRoute path="/portal/dashboard" component={Dashboard} />
          )}
          <PrivateRoute path="/portal/management" component={Management} />
          {userPermissions?.['create-report']?.see && (
            <PrivateRoute path="/portal/reports" component={Reports} />
          )}

          <Route
            exact
            path="/portal"
            render={props => (
              <Redirect
                to={{
                  pathname: '/portal/login',
                  search: props.location.search,
                }}
              />
            )}
          />
          <Route
            exact
            path="/"
            render={props => (
              <Redirect
                to={{
                  pathname: '/portal/login',
                  search: props.location.search,
                }}
              />
            )}
          />
          <Route
            path="*"
            render={props => <NotFound history={props.history} />}
          />
        </Switch>
      </Suspense>
    </Box>
  );
};

export default Routes;
